import { useCallback, useEffect, useState } from 'react';

import { listArtists, setArtistApproval, type AdminArtist } from '../../lib/admin';
import { ApiError } from '../../lib/apiClient';
import { formatDateShort } from '../../lib/format';
import './AdminPages.css';

/**
 * Artist approval queue.
 *
 * Until approved, an artist profile is invisible everywhere public: the artist
 * directory hard-filters approved = true, and the follow endpoint rejects an
 * unapproved artist outright. Approval is what lets them be booked and appear
 * in a lineup.
 */
export function AdminArtists() {
  const [tab, setTab] = useState<'false' | 'true' | 'all'>('false');
  const [artists, setArtists] = useState<AdminArtist[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    listArtists(tab)
      .then(setArtists)
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Could not load artists.');
        setArtists([]);
      })
      .finally(() => setLoading(false));
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  async function decide(artist: AdminArtist, approved: boolean) {
    if (!approved && !window.confirm(
      `Revoke approval for ${artist.stageName}?\n\n`
      + 'They will disappear from the public directory and cannot be booked or '
      + 'added to a lineup until approved again.',
    )) return;

    setBusyId(artist.artistProfileId);
    setError(null);
    try {
      const result = await setArtistApproval(artist.artistProfileId, approved);
      setArtists((cur) =>
        cur?.map((a) =>
          a.artistProfileId === artist.artistProfileId
            ? { ...a, approved: result.approved }
            : a,
        ) ?? cur,
      );
      // A decided artist leaves the pending tab.
      if (tab !== 'all') {
        setArtists((cur) =>
          cur?.filter((a) => a.artistProfileId !== artist.artistProfileId) ?? cur,
        );
      }
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Could not update that artist.');
    } finally {
      setBusyId(null);
    }
  }

  const num = (v: number | null) => (v == null ? ' | ' : v.toLocaleString('en-US'));

  return (
    <div className="adm-page">
      <header className="adm-header">
        <div>
          <h1 className="adm-title">Artist approval</h1>
          <p className="adm-lede">
            Unapproved artists are hidden from the public directory and cannot be booked.
          </p>
        </div>
        {artists && tab === 'false' && (
          <div className="adm-stat">
            <span className="adm-stat__value mono">{artists.length}</span>
            <span className="adm-stat__label">awaiting review</span>
          </div>
        )}
      </header>

      <div className="adm-filters">
        {([['false', 'Pending'], ['true', 'Approved'], ['all', 'All']] as const).map(([v, label]) => (
          <button
            key={v}
            type="button"
            className={`adm-tab${tab === v ? ' is-active' : ''}`}
            onClick={() => setTab(v)}
          >
            {label}
          </button>
        ))}
      </div>

      {error && <p className="adm-status adm-error" role="alert">{error}</p>}
      {loading && <p className="adm-status">Loading…</p>}
      {!loading && artists?.length === 0 && (
        <p className="adm-status adm-muted">
          {tab === 'false' ? 'Nothing awaiting review.' : 'No artists here.'}
        </p>
      )}

      <div className="adm-list">
        {artists?.map((a) => (
          <article key={a.artistProfileId} className="adm-card">
            <header className="adm-card__head">
              <div className="adm-artist">
                <span className="adm-artist__avatar" aria-hidden="true">
                  {a.profileImageUrl
                    ? <img src={a.profileImageUrl} alt="" />
                    : a.stageName.slice(0, 1)}
                </span>
                <div>
                  <h2 className="adm-card__title">{a.stageName}</h2>
                  <p className="adm-card__meta">
                    {a.genre ?? 'No genre'}
                    {a.primaryMarket ? ` · ${a.primaryMarket}` : ''}
                    {' · '}applied {formatDateShort(a.createdAt)}
                    {a.tier ? ` · ${a.tier}` : ''}
                  </p>
                  {a.account && (
                    <p className="adm-card__meta adm-muted">
                      {a.account.name || '-'} <span className="mono">{a.account.email}</span>
                      {!a.account.verified && (
                        <span className="adm-pill adm-pill--pending">Email unverified</span>
                      )}
                    </p>
                  )}
                </div>
              </div>

              <div className="adm-card__status">
                <span className={`adm-pill adm-pill--${a.approved ? 'approved' : 'pending'}`}>
                  {a.approved ? 'Approved' : 'Pending'}
                </span>
              </div>
            </header>

            <div className="adm-card__body">
              {a.bio && <p className="adm-bio">{a.bio}</p>}

              <dl className="adm-metrics">
                <div>
                  <dt>Instagram</dt>
                  <dd className="mono">
                    {a.instagramHandle ? `@${a.instagramHandle}` : ' | '}
                    {a.instagramFollowers ? ` · ${num(a.instagramFollowers)}` : ''}
                  </dd>
                </div>
                <div>
                  <dt>Spotify monthly</dt>
                  <dd className="mono">{num(a.spotifyMonthlyListeners)}</dd>
                </div>
                <div>
                  <dt>Booking history</dt>
                  <dd className="mono">{a.agreements} agreement{a.agreements === 1 ? '' : 's'}</dd>
                </div>
              </dl>

              <div className="adm-card__actions">
                {!a.approved ? (
                  <button
                    type="button"
                    className="button button--primary adm-btn-sm"
                    disabled={busyId === a.artistProfileId}
                    onClick={() => decide(a, true)}
                  >
                    {busyId === a.artistProfileId ? 'Saving…' : 'Approve artist'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="button button--ghost adm-btn-sm"
                    disabled={busyId === a.artistProfileId}
                    onClick={() => decide(a, false)}
                  >
                    Revoke approval
                  </button>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
